// Shared audio analysis utilities for Recorder and other audio components

export async function analyzeAudioLevel(audioBlob) {
  return new Promise((resolve) => {
    try {
      if (!audioBlob || audioBlob.size < 1000) {
        resolve({ isSilent: true, reason: 'file_too_small', size: audioBlob ? audioBlob.size : 0 });
        return;
      }

      const audioContext = new ((typeof window !== 'undefined' && window.AudioContext) || (typeof window !== 'undefined' && window.webkitAudioContext))();
      const fileReader = new FileReader();

      const timeout = setTimeout(() => {
        resolve({ isSilent: true, reason: 'analysis_timeout' });
      }, 3000);

      fileReader.onload = async (e) => {
        try {
          clearTimeout(timeout);
          const arrayBuffer = e.target.result;
          if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            resolve({ isSilent: true, reason: 'empty_buffer' });
            return;
          }
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          const audioData = audioBuffer.getChannelData(0);
          const sr = audioBuffer.sampleRate || 44100;

          let sum = 0;
          let maxAbs = 0;
          let energyVariance = 0;
          let zeroCrossings = 0;
          const sampleStep = Math.max(1, Math.floor(audioData.length / 2000));
          const samples = [];
          const absValues = [];
          let voicedFrames = 0;
          for (let i = 0; i < audioData.length; i += sampleStep) {
            const abs = Math.abs(audioData[i]);
            sum += abs;
            if (abs > maxAbs) maxAbs = abs;
            samples.push(abs);
            absValues.push(abs);
            if (abs > 0.01) voicedFrames++;
            if (i > 0 && ((audioData[i] >= 0) !== (audioData[i - sampleStep] >= 0))) zeroCrossings++;
          }

          const avgAmplitude = samples.length ? sum / samples.length : 0;
          for (let i = 0; i < samples.length; i++) energyVariance += Math.pow(samples[i] - avgAmplitude, 2);
          energyVariance = samples.length ? (energyVariance / samples.length) : 0;
          const zcr = samples.length ? (zeroCrossings / samples.length) : 0;
          const speechFraction = samples.length ? (voicedFrames / samples.length) : 0;

          absValues.sort((a, b) => a - b);
          const q = (p) => absValues.length ? absValues[Math.max(0, Math.min(absValues.length - 1, Math.floor(p * (absValues.length - 1))))] : 0;
          const p95 = q(0.95);
          const p05 = q(0.05);
          const dynamicRange = Math.max(0, p95 - p05);

          const windowSamples = Math.max(1, Math.floor(sr * 0.02));
          let longestRun = 0;
          let currentRun = 0;
          for (let start = 0; start < audioData.length; start += windowSamples) {
            let sumSq = 0;
            let n = 0;
            for (let j = start; j < Math.min(start + windowSamples, audioData.length); j++) {
              const v = audioData[j];
              sumSq += v * v;
              n++;
            }
            const rms = n ? Math.sqrt(sumSq / n) : 0;
            if (rms > 0.015) {
              currentRun++;
              if (currentRun > longestRun) longestRun = currentRun;
            } else {
              currentRun = 0;
            }
          }
          const longestRunSec = (longestRun * windowSamples) / sr;

          const hasVariation = energyVariance > 0.00003;
          const hasSpeechZCR = zcr > 0.005 && zcr < 0.9;
          const hasReasonableAmplitude = avgAmplitude > 0.002 && avgAmplitude < 0.8;
          const hasReasonablePeak = maxAbs > 0.015 && maxAbs < 1.0;
          const hasEnoughVoiced = speechFraction >= 0.12;
          const hasDynamicRange = dynamicRange >= 0.025;
          const hasContinuousSpeech = longestRunSec >= 0.15;

          const isSilent = !(hasEnoughVoiced && hasDynamicRange && hasContinuousSpeech && hasReasonablePeak && hasSpeechZCR && hasVariation && hasReasonableAmplitude);

          resolve({
            isSilent,
            avgAmplitude,
            maxAbs,
            energyVariance,
            zcr,
            speechFraction,
            dynamicRange,
            longestRunSec,
            reason: isSilent ? 'no_speech_detected' : 'speech_detected'
          });
        } catch (err) {
          resolve({ isSilent: false, reason: 'analysis_error', error: err?.message });
        }
      };

      fileReader.onerror = () => {
        clearTimeout(timeout);
        resolve({ isSilent: false, reason: 'file_read_error' });
      };

      fileReader.readAsArrayBuffer(audioBlob);
    } catch (err) {
      resolve({ isSilent: false, reason: 'setup_error', error: err?.message });
    }
  });
}

export function isLikelyNoiseResult(apiData) {
  try {
    const res = (apiData && (apiData.RES || apiData.res || apiData.data || apiData)) || {};
    const top5 = String(res.top_5 || '');
    const matches = top5.match(/(\d+(?:\.\d+)?)%/g) || [];
    const values = matches.map(m => parseFloat(m.replace('%', '')) / 100).filter(v => Number.isFinite(v));
    if (!values.length) return true;
    const maxVal = Math.max(...values);
    const above10 = values.filter(v => v >= 0.10).length;
    const sum = values.reduce((a, b) => a + b, 0);
    if (maxVal < 0.18 && above10 <= 1) return true;
    if (sum < 0.60) return true;
    return false;
  } catch {
    return false;
  }
}

export function encodeWAV(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  function writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  const floatTo16BitPCM = (output, offset, input) => {
    for (let i = 0; i < input.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, input[i]));
      s = s < 0 ? s * 0x8000 : s * 0x7FFF;
      output.setInt16(offset, s, true);
    }
  };

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);

  floatTo16BitPCM(view, 44, samples);

  return new Blob([view], { type: "audio/wav" });
}
